/**
 * GPACL Sentinel Values — shared between all frontend modules.
 *
 * These must stay in sync with the PHP Sentinel_Registry registrations.
 * When a new module adds sentinels in PHP, add them here too.
 *
 * @package
 */

/**
 * All GPACL sentinel value strings.
 */
export const SENTINELS = {
	// Advanced Logic
	EVALUATE: 'gpacl_evaluate',
	PASS: 'gpacl_pass',

	// Formula Rule (registered by Advanced Logic since it's the only caller)
	FORMULA_PASS: 'gpacl_formula_pass',
	FORMULA_FAIL: 'gpacl_formula_fail',

	// User Logic
	USER_PASS: 'gpacl_user_pass',
	USER_FAIL: 'gpacl_user_fail',
} as const;

export type SentinelValue = (typeof SENTINELS)[keyof typeof SENTINELS];

/**
 * Prefix applied to target field IDs in the primary evaluate rule.
 */
export const SENTINEL_FIELD_PREFIX = 'gpacl_field_';

/**
 * Recover the target field ID from a prefixed evaluate rule `fieldId`.
 *
 * @param sentinelId The evaluate rule's fieldId.
 * @return The bare field ID, or the input unchanged when unprefixed.
 */
export function sentinelTargetToFieldId(
	sentinelId: string | number
): string | number {
	if (
		typeof sentinelId === 'string' &&
		sentinelId.startsWith(SENTINEL_FIELD_PREFIX)
	) {
		return parseInt(sentinelId.slice(SENTINEL_FIELD_PREFIX.length), 10);
	}

	return sentinelId;
}

const ALL_PASS_SENTINELS: readonly string[] = [
	SENTINELS.PASS,
	SENTINELS.FORMULA_PASS,
	SENTINELS.USER_PASS,
];

const ALL_FAIL_SENTINELS: readonly string[] = [
	SENTINELS.FORMULA_FAIL,
	SENTINELS.USER_FAIL,
];

const ALL_SENTINELS: readonly string[] = [
	...ALL_PASS_SENTINELS,
	...ALL_FAIL_SENTINELS,
	SENTINELS.EVALUATE,
];

/**
 * Check if a value is a pass sentinel (always evaluates to true).
 *
 * @param value The value to check.
 */
export function isPassSentinel(value: string): boolean {
	return ALL_PASS_SENTINELS.includes(value);
}

/**
 * Check if a value is a fail sentinel (always evaluates to false).
 *
 * @param value The value to check.
 */
export function isFailSentinel(value: string): boolean {
	return ALL_FAIL_SENTINELS.includes(value);
}

/**
 * Check if a value is any registered GPACL sentinel.
 *
 * @param value The value to check.
 */
export function isAnySentinel(value: string): boolean {
	return ALL_SENTINELS.includes(value);
}
